import React, {
  useEffect, useState, useRef, useCallback,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { publicQueue } from "../../api";
import { AudioEngine } from "./AudioEngine";

const THEMES = {
  dark: {
    bg: "#060D1F", surface: "rgba(15,23,42,0.97)", border: "rgba(37,99,235,0.2)",
    accent: "#2563EB", ticketNum: "#FFFFFF", label: "#64748B", text: "#E2E8F0",
    sub: "#475569", headerBg: "#0A1023", banner: "#1E3A5F", bannerText: "#93C5FD",
    idle: "rgba(30,41,59,0.5)", panelBg: "#0D1628", panelHeader: "#111827",
    upNextHeader: "#1E1B4B", recentHeader: "#052E16", returningHeader: "#431407",
    stationStatus: "#0F172A", stationCard: "rgba(15,23,42,0.9)",
    serving: "#2563EB", available: "#16A34A",
    footerBg: "#1E3A5F", footerText: "#BFDBFE",
  },
  dark_blue: {
    bg: "#040C1C", surface: "rgba(10,20,50,0.97)", border: "rgba(59,130,246,0.25)",
    accent: "#3B82F6", ticketNum: "#FFFFFF", label: "#4B6CB7", text: "#E2E8F0",
    sub: "#4B6CB7", headerBg: "#05122E", banner: "#1E3A8A", bannerText: "#BFDBFE",
    idle: "rgba(15,30,70,0.5)", panelBg: "#071035", panelHeader: "#0A1845",
    upNextHeader: "#1E1B4B", recentHeader: "#052E16", returningHeader: "#431407",
    stationStatus: "#050E28", stationCard: "rgba(10,20,50,0.9)",
    serving: "#3B82F6", available: "#16A34A",
    footerBg: "#1E3A8A", footerText: "#BFDBFE",
  },
  light: {
    bg: "#EFF6FF", surface: "rgba(255,255,255,0.98)", border: "rgba(37,99,235,0.15)",
    accent: "#1D4ED8", ticketNum: "#0F172A", label: "#94A3B8", text: "#0F172A",
    sub: "#64748B", headerBg: "#FFFFFF", banner: "#1D4ED8", bannerText: "#FFFFFF",
    idle: "rgba(226,232,240,0.6)", panelBg: "#F1F5F9", panelHeader: "#E2E8F0",
    upNextHeader: "#312E81", recentHeader: "#14532D", returningHeader: "#7C2D12",
    stationStatus: "#E2E8F0", stationCard: "rgba(255,255,255,0.95)",
    serving: "#1D4ED8", available: "#16A34A",
    footerBg: "#1D4ED8", footerText: "#FFFFFF",
  },
};

const FONT_SIZES = { small: 0.82, normal: 1, large: 1.2, xlarge: 1.45 };
const STATION_COLORS = ["#3B82F6","#10B981","#F59E0B","#8B5CF6","#EF4444","#06B6D4","#EC4899","#84CC16"];

export default function DisplayBoardPage() {
  const { slug }          = useParams();
  const [searchParams]    = useSearchParams();
  const zoneFilter        = searchParams.get("zone");

  const [state,   setState]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const unlockedRef    = useRef(false);
  const [unlocked, setUnlocked] = useState(false);
  const prevServingRef = useRef({});
  const engineRef      = useRef(null);
  const pollRef        = useRef(null);
  const mountedRef     = useRef(true);

  // Station status pagination
  const [stationPage, setStationPage] = useState(0);
  const STATIONS_PER_PAGE = 5;

  // Up Next scroll
  const upNextScrollRef = useRef(null);
  const scrollAnimRef   = useRef(null);

  const poll = useCallback(async (since = null) => {
    if (!mountedRef.current) return;
    try {
      const params = {
        ...(since ? { since } : {}),
        ...(zoneFilter ? { zone: zoneFilter } : {}),
      };
      const { data } = await publicQueue.displayState(slug, params);
      if (!mountedRef.current) return;
      setState(data);
      setError(null);
      setLoading(false);

      if (data.settings || data.audio?.length) {
        if (!engineRef.current) {
          engineRef.current = new AudioEngine(data.audio || [], data.settings || {});
        } else {
          engineRef.current.setAssets(data.audio || []);
          engineRef.current.settings = data.settings || {};
          engineRef.current.volume   = (data.settings?.board_audio_volume ?? 80) / 100;
          engineRef.current.language = data.settings?.board_audio_language || "en";
        }
      }

      if (unlockedRef.current) {
        detectNewCalls(data, prevServingRef.current, engineRef.current, data.settings);
      }

      const nowServing = {};
      (data.zones || []).forEach(zone =>
        (zone.stations || []).forEach(station =>
          (station.counters || []).forEach(counter => {
            if (counter.serving) nowServing[counter.id] = counter.serving.ticket_number;
          })
        )
      );
      prevServingRef.current = nowServing;

      const ts = data.summary?.timestamp;
      pollRef.current = setTimeout(() => poll(ts), 200);
    } catch {
      if (!mountedRef.current) return;
      setLoading(false);
      setError("Connection lost — retrying…");
      pollRef.current = setTimeout(() => poll(null), 5000);
    }
  }, [slug, zoneFilter]);

  useEffect(() => {
    mountedRef.current = true;
    poll();
    return () => {
      mountedRef.current = false;
      clearTimeout(pollRef.current);
      window.speechSynthesis?.cancel();
    };
  }, [poll]);

  // Fullscreen management
  useEffect(() => {
    if (!state?.settings) return;
    const s = state.settings;
    if (!s.board_fullscreen) return;

    const tryFullscreen = () => {
      if (document.fullscreenElement) return;
      document.documentElement.requestFullscreen().catch(() => {});
    };

    // Auto re-enter if exited
    const onFsChange = () => {
      if (!document.fullscreenElement && s.board_auto_reenter_fullscreen) {
        setTimeout(tryFullscreen, 1000);
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, [state?.settings]);

  // Hide cursor
  useEffect(() => {
    if (!state?.settings) return;
    if (state.settings.board_hide_cursor) {
      document.body.style.cursor = "none";
    }
    return () => { document.body.style.cursor = ""; };
  }, [state?.settings]);

  // Wake Lock (prevent screen sleep)
  useEffect(() => {
    if (!state?.settings?.board_prevent_sleep) return;
    let wakeLock = null;
    const acquire = async () => {
      try {
        wakeLock = await navigator.wakeLock?.request("screen");
      } catch {}
    };
    acquire();
    const reacquire = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", reacquire);
    return () => {
      document.removeEventListener("visibilitychange", reacquire);
      wakeLock?.release().catch(() => {});
    };
  }, [state?.settings]);

  // Heartbeat — ping server every 60s
  useEffect(() => {
    if (!state?.settings?.display_token && !slug) return;
    const token     = state?.settings?.display_token || slug;
    const zoneName  = zoneFilter ? (state?.zones?.[0]?.name || "Zone Board") : "Full Board";
    const ping = () => {
      fetch("/api/facilities/heartbeat/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          zone_id:   zoneFilter || "",
          zone_name: zoneName,
          label:     zoneName,
        }),
      }).catch(() => {});
    };
    ping();
    const interval = setInterval(ping, 60000);
    return () => clearInterval(interval);
  }, [slug, zoneFilter, state?.settings?.display_token]);

  // Auto-scroll Up Next
  useEffect(() => {
    const el = upNextScrollRef.current;
    if (!el) return;
    let pos = 0;
    const step = () => {
      if (!el) return;
      pos += 0.5;
      if (pos >= el.scrollHeight - el.clientHeight) pos = 0;
      el.scrollTop = pos;
      scrollAnimRef.current = requestAnimationFrame(step);
    };
    scrollAnimRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(scrollAnimRef.current);
  }, [state]);

  const handleUnlock = () => {
    unlockedRef.current = true;
    setUnlocked(true);
    // Enter fullscreen on first tap (required user gesture)
    if (settings.board_fullscreen !== false) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) new Ctx().resume();
    } catch {}
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      window.speechSynthesis.speak(u);
    }
  };

  const settings       = state?.settings || {};
  const theme          = THEMES[settings.board_theme] || THEMES.dark;
  const fontScale      = FONT_SIZES[settings.board_font_size] || 1;
  const cols           = Math.max(1, Math.min(8, settings.board_counters_per_row || 3));
  const summary        = state?.summary || {};
  const displayZones   = state?.zones || [];
  const recentlyCalled = state?.recently_called || [];
  const returningPts   = state?.returning_patients || [];

  // Flat lists
  const nowServing = [];
  const allStations = [];
  displayZones.forEach(z => {
    z.stations?.forEach(st => {
      allStations.push(st);
      st.counters?.forEach(c => {
        if (c.serving) nowServing.push({ ...c.serving, counterName: c.name, stationName: st.name });
      });
    });
  });

  const upNext = collectUpNext(displayZones, returningPts);
  const totalStationPages = Math.ceil(allStations.length / STATIONS_PER_PAGE);
  const visibleStations = allStations.slice(
    stationPage * STATIONS_PER_PAGE,
    (stationPage + 1) * STATIONS_PER_PAGE
  );

  if (loading && !state) return <FullLoader />;
  if (error && !state) {
    return (
      <div style={{ ...S.fullScreen, background: "#060D1F" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "44px", marginBottom: "12px" }}>📡</div>
          <div style={{ color: "#EF4444", fontSize: "16px" }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...S.root, background: theme.bg, fontSize: `${fontScale}rem` }}>

      {/* Audio unlock overlay */}
      {!unlocked && (
        <div style={S.unlockOverlay} onClick={handleUnlock}>
          <div style={S.unlockCard}>
            <div style={{ fontSize: "52px", marginBottom: "16px" }}>🔊</div>
            <div style={{ fontSize: "22px", fontWeight: "700", color: "#fff", marginBottom: "10px" }}>
              Tap to activate display board
            </div>
            <div style={{ fontSize: "14px", color: "#64748B", maxWidth: "320px", textAlign: "center", lineHeight: "1.7" }}>
              Browsers require a user interaction to enable audio.
            </div>
          </div>
        </div>
      )}

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <div style={{ ...S.header, background: theme.headerBg, borderBottom: `2px solid ${theme.border}` }}>
        <div style={S.headerLeft}>
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none" style={{ flexShrink: 0 }}>
            <rect width="32" height="32" rx="8" fill={theme.accent}/>
            <path d="M8 16C8 11.58 11.58 8 16 8V12C13.79 12 12 13.79 12 16C12 18.21 13.79 20 16 20V24C11.58 24 8 20.42 8 16Z" fill="white"/>
            <path d="M16 8C20.42 8 24 11.58 24 16H20C20 13.79 18.21 12 16 12V8Z" fill="#93C5FD"/>
          </svg>
          <div>
            <div style={{ fontSize: "1em", fontWeight: "700", color: theme.text, letterSpacing: "-0.3px" }}>Queue Display</div>
            {settings.facility_name && <div style={{ fontSize: "0.7em", color: theme.sub }}>{settings.facility_name}</div>}
          </div>
        </div>

        {settings.board_show_stats !== false && (
          <div style={S.statsRow}>
            <StatPill label="Waiting"    value={summary.total_waiting}   accent="#EAB308" theme={theme} />
            <StatPill label="Serving"    value={summary.total_serving}   accent={theme.accent} theme={theme} />
            <StatPill label="Done Today" value={summary.total_completed} accent="#22C55E" theme={theme} />
          </div>
        )}
        <LiveClock theme={theme} />
      </div>

      {/* ── ANNOUNCEMENT BANNER ────────────────────────────────────────────── */}
      {settings.board_announcement_active && settings.board_announcement && (
        <div style={{
          ...S.bannerBar,
          background: theme.banner,
          borderBottom: `2px solid ${theme.border}`,
          zIndex: 10,
          position: "relative",
        }}>
          <span style={{ fontSize: "1em", flexShrink: 0 }}>📢</span>
          <marquee style={{ ...S.bannerText, color: theme.bannerText }}>
            {settings.board_announcement}
          </marquee>
        </div>
      )}

      {/* ── MAIN BODY ──────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>

        {/* Left: Now Serving */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", borderRight: `2px solid ${theme.border}`, overflow: "hidden" }}>
          <div style={{ ...S.panelHeader, background: theme.accent }}>
            <span style={{ fontSize: "1em" }}>🔊</span>
            <span style={S.panelHeaderText}>NOW SERVING</span>
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {nowServing.length === 0 ? (
              <div style={{ padding: "40px 24px", textAlign: "center", color: theme.sub, fontSize: "0.85em" }}>
                No patients currently being served
              </div>
            ) : nowServing.map((s, i) => (
              <div key={i} style={{ ...S.servingRow, borderBottom: `1px solid ${theme.border}`, background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
                <div style={{
                  fontSize: `${fontScale * 2.8}em`,
                  fontWeight: "900",
                  color: s.priority === "emergency" ? "#EF4444" : s.priority === "priority" ? "#F59E0B" : theme.ticketNum,
                  letterSpacing: "-2px", lineHeight: 1,
                  fontVariantNumeric: "tabular-nums", minWidth: "120px",
                }}>
                  {s.ticket_number}
                </div>
                <div style={{ fontSize: "1.6em", color: theme.sub, fontWeight: "300" }}>→</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "1.1em", fontWeight: "700", color: theme.text }}>{s.stationName}</div>
                  <div style={{ fontSize: "0.8em", color: theme.accent, marginTop: "2px" }}>{s.counterName}</div>
                  {s.staff_name && settings.board_show_staff_names && (
                    <div style={{ fontSize: "0.7em", color: theme.sub, marginTop: "2px" }}>{s.staff_name}</div>
                  )}
                </div>
                {s.priority !== "normal" && (
                  <div style={{
                    fontSize: "0.65em", fontWeight: "700", padding: "4px 10px",
                    borderRadius: "6px",
                    background: s.priority === "emergency" ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.15)",
                    color: s.priority === "emergency" ? "#EF4444" : "#F59E0B",
                  }}>
                    {s.priority === "emergency" ? "🚨 EMERGENCY" : "⚡ PRIORITY"}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right column 1: Up Next (top) + Recently Called (bottom) */}
        <div style={{ width: "220px", flexShrink: 0, display: "flex", flexDirection: "column", borderRight: returningPts.length > 0 ? `2px solid ${theme.border}` : "none", overflow: "hidden" }}>

          {/* Up Next — scrolling */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", borderBottom: `2px solid ${theme.border}`, overflow: "hidden" }}>
            <div style={{ ...S.panelHeader, background: theme.upNextHeader }}>
              <span style={{ fontSize: "0.9em" }}>≡</span>
              <span style={S.panelHeaderText}>UP NEXT</span>
            </div>
            <div
              ref={upNextScrollRef}
              style={{ flex: 1, overflowY: "hidden" }}
            >
              {settings.board_show_up_next !== false && upNext.length > 0 ? upNext.map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", borderBottom: `0.5px solid ${theme.border}` }}>
                  <span style={{ fontSize: "0.7em", color: theme.sub, minWidth: "16px", fontWeight: "700" }}>{i + 1}</span>
                  <span style={{ fontSize: "1em", fontWeight: "800", color: "#7C77E0", flex: 1, fontVariantNumeric: "tabular-nums" }}>{t.ticket_number}</span>
                  <span style={{ fontSize: "0.65em", color: theme.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "80px", textAlign: "right" }}>{t.station_name}</span>
                  {t.priority !== "normal" && <span style={{ width: "7px", height: "7px", borderRadius: "50%", flexShrink: 0, background: t.priority === "emergency" ? "#EF4444" : "#EAB308" }} />}
                </div>
              )) : (
                <div style={{ padding: "24px 14px", color: theme.sub, fontSize: "0.7em", textAlign: "center" }}>No patients queued</div>
              )}
            </div>
          </div>

          {/* Recently Called */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ ...S.panelHeader, background: theme.recentHeader }}>
              <span style={{ fontSize: "0.9em" }}>🕐</span>
              <span style={S.panelHeaderText}>RECENTLY CALLED</span>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {recentlyCalled.length === 0 ? (
                <div style={{ padding: "24px 14px", color: theme.sub, fontSize: "0.7em", textAlign: "center" }}>No calls yet today</div>
              ) : recentlyCalled.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "9px 14px", borderBottom: `0.5px solid ${theme.border}` }}>
                  <div>
                    <div style={{ fontSize: "0.95em", fontWeight: "800", color: "#10B981", fontVariantNumeric: "tabular-nums" }}>{r.ticket_number}</div>
                    <div style={{ fontSize: "0.65em", color: theme.text, marginTop: "1px" }}>{r.station_name}</div>
                    {r.counter_name && <div style={{ fontSize: "0.6em", color: theme.sub }}>{r.counter_name}</div>}
                  </div>
                  <div style={{ fontSize: "0.6em", color: theme.sub, flexShrink: 0 }}>{r.called_at}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column 2: Returning Patients (conditional) */}
        {returningPts.length > 0 && (
          <div style={{ width: "200px", flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ ...S.panelHeader, background: theme.returningHeader }}>
              <span style={{ fontSize: "0.9em" }}>↩</span>
              <span style={S.panelHeaderText}>RETURNING</span>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {returningPts.map((r, i) => (
                <div key={i} style={{ padding: "10px 14px", borderBottom: `0.5px solid ${theme.border}` }}>
                  <div style={{ fontSize: "1em", fontWeight: "800", color: "#F97316", fontVariantNumeric: "tabular-nums", marginBottom: "4px" }}>{r.ticket_number}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.65em" }}>
                    <span style={{ color: theme.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60px" }}>{r.from_station}</span>
                    <span style={{ color: "#F97316", flexShrink: 0 }}>→</span>
                    <span style={{ color: theme.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70px" }}>{r.to_station}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── STATION STATUS ─────────────────────────────────────────────────── */}
      <div style={{ background: theme.stationStatus, borderTop: `2px solid ${theme.border}`, flexShrink: 0 }}>
        {/* Station status header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 18px", borderBottom: `1px solid ${theme.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "0.75em" }}>🖥</span>
            <span style={{ fontSize: "0.7em", fontWeight: "700", color: theme.label, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Station Status
            </span>
          </div>
          {totalStationPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "0.6em", color: theme.sub }}>
                Page {stationPage + 1} of {totalStationPages}
              </span>
              <button
                onClick={() => setStationPage(p => Math.max(0, p - 1))}
                disabled={stationPage === 0}
                style={{ ...S.pageBtn, opacity: stationPage === 0 ? 0.3 : 1 }}
              >‹</button>
              <button
                onClick={() => setStationPage(p => Math.min(totalStationPages - 1, p + 1))}
                disabled={stationPage >= totalStationPages - 1}
                style={{ ...S.pageBtn, opacity: stationPage >= totalStationPages - 1 ? 0.3 : 1 }}
              >›</button>
            </div>
          )}
        </div>

        {/* Station cards */}
        <div style={{ display: "flex", gap: "12px", padding: "12px 18px", overflowX: "auto" }}>
          {visibleStations.map((st, i) => {
            const col = STATION_COLORS[(stationPage * STATIONS_PER_PAGE + i) % STATION_COLORS.length];
            return (
              <div key={st.id} style={{
                background: theme.stationCard,
                border: `1px solid ${col}33`,
                borderTop: `3px solid ${col}`,
                borderRadius: "10px",
                minWidth: "160px",
                flex: 1,
                overflow: "hidden",
              }}>
                {/* Station name header */}
                <div style={{ padding: "8px 12px", borderBottom: `1px solid ${col}22` }}>
                  <div style={{ fontSize: "0.75em", fontWeight: "700", color: col, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {st.name} ({st.counters?.length || 0})
                  </div>
                </div>
                {/* Counters */}
                <div style={{ padding: "6px 12px 10px" }}>
                  {st.counters?.map(c => (
                    <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                      <span style={{ fontSize: "0.72em", color: theme.sub, fontWeight: "500" }}>{c.name}</span>
                      {c.is_away ? (
                        <span style={{ fontSize: "0.65em", color: "#D97706", fontWeight: "700", background: "rgba(217,119,6,0.12)", padding: "2px 7px", borderRadius: "4px" }}>Break</span>
                      ) : c.serving ? (
                        <span style={{ fontSize: "0.7em", fontWeight: "700", color: "#fff", background: theme.serving, padding: "2px 8px", borderRadius: "4px", fontVariantNumeric: "tabular-nums" }}>
                          {c.serving.ticket_number}
                        </span>
                      ) : (
                        <span style={{ fontSize: "0.65em", fontWeight: "700", color: theme.available }}>Available</span>
                      )}
                    </div>
                  ))}
                  {(!st.counters || st.counters.length === 0) && (
                    <div style={{ fontSize: "0.65em", color: theme.sub, fontStyle: "italic" }}>No counters</div>
                  )}
                </div>
              </div>
            );
          })}
          {allStations.length === 0 && (
            <div style={{ color: theme.sub, fontSize: "0.7em", padding: "8px 0" }}>No stations configured.</div>
          )}
        </div>
      </div>

      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <div style={{ ...S.footer, background: theme.footerBg, borderTop: `2px solid ${theme.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
          {settings.branding_logo_url && (
            <img src={settings.branding_logo_url} alt="logo"
              style={{ height: "28px", objectFit: "contain", flexShrink: 0 }} />
          )}
          <span style={{ fontSize: "0.8em", fontWeight: "600", color: theme.footerText }}>
            {settings.footer_message ||
             settings.board_announcement ||
             "Please listen for your number and proceed to the assigned counter. Thank you!"}
          </span>
        </div>
        <div style={{ fontSize: "0.7em", fontWeight: "700", color: theme.footerText, opacity: 0.7, flexShrink: 0 }}>
          {settings.powered_by_text
            ? settings.powered_by_text
            : settings.board_show_powered_by !== false
              ? `♥ Powered by ${settings.branding_name || "EliyaQ"}`
              : null
          }
        </div>
      </div>

      {error && <div style={S.errorRibbon}>⚠ {error}</div>}
    </div>
  );
}

function StatPill({ label, value, accent, theme }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", background: `${accent}18`, border: `1px solid ${accent}33`, borderRadius: "10px", padding: "8px 16px", minWidth: "70px" }}>
      <div style={{ fontSize: "1.5em", fontWeight: "800", color: accent, lineHeight: 1 }}>{value ?? 0}</div>
      <div style={{ fontSize: "0.6em", color: theme.sub, fontWeight: "700", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

function LiveClock({ theme }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const day = time.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const clk = time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <div style={{ textAlign: "right", flexShrink: 0 }}>
      <div style={{ fontSize: "1.8em", fontWeight: "800", color: theme.text, letterSpacing: "-0.5px", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{clk}</div>
      <div style={{ fontSize: "0.6em", color: theme.sub, marginTop: "2px" }}>{day}</div>
    </div>
  );
}

function FullLoader() {
  return (
    <div style={{ ...S.fullScreen, background: "#060D1F", flexDirection: "column", gap: "16px" }}>
      <div style={{ width: "48px", height: "48px", border: "3px solid rgba(37,99,235,0.2)", borderTopColor: "#2563EB", borderRadius: "50%", animation: "eq-spin 0.7s linear infinite" }} />
      <div style={{ color: "#475569", fontSize: "16px" }}>Loading display board…</div>
    </div>
  );
}

function collectUpNext(zones, returningPts) {
  // Exclude returning patients from up_next — they show in the RETURNING column
  const returningNumbers = new Set((returningPts || []).map(r => r.ticket_number));
  const all = [];
  zones.forEach(zone =>
    zone.stations?.forEach(station =>
      station.up_next?.forEach(t => {
        if (!returningNumbers.has(t.ticket_number)) {
          all.push({ ...t, station_name: station.name });
        }
      })
    )
  );
  return all.sort((a, b) => {
    const p = { emergency: 0, priority: 1, normal: 2 };
    if (p[a.priority] !== p[b.priority]) return p[a.priority] - p[b.priority];
    return new Date(a.issued_at) - new Date(b.issued_at);
  });
}

function detectNewCalls(data, prev, engine, settings) {
  if (!engine) return;
  const lang = settings?.board_audio_language || "en";
  (data.zones || []).forEach(zone =>
    (zone.stations || []).forEach(station =>
      (station.counters || []).forEach(counter => {
        if (!counter.serving) return;
        if (prev[counter.id] === counter.serving.ticket_number) return;
        engine.announce(counter.serving.ticket_number, station.name, lang, counter.serving.staff_name || "");
      })
    )
  );
}

const S = {
  root: { minHeight: "100vh", height: "100vh", display: "flex", flexDirection: "column", fontFamily: "'DM Sans','Segoe UI',sans-serif", userSelect: "none", overflow: "hidden" },
  fullScreen: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif" },
  unlockOverlay: { position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.88)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  unlockCard: { background: "rgba(15,23,42,0.97)", border: "1px solid rgba(37,99,235,0.3)", borderRadius: "24px", padding: "48px 52px", display: "flex", flexDirection: "column", alignItems: "center", boxShadow: "0 30px 80px rgba(0,0,0,0.7)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 24px", flexShrink: 0, gap: "16px" },
  headerLeft: { display: "flex", alignItems: "center", gap: "12px" },
  statsRow: { display: "flex", gap: "10px" },
  bannerBar: { display: "flex", alignItems: "center", gap: "12px", padding: "10px 24px", flexShrink: 0, overflow: "hidden" },
  bannerText: { fontSize: "0.9em", fontWeight: "600", whiteSpace: "nowrap", animation: "eq-marquee 22s linear infinite" },
  panelHeader: { display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", flexShrink: 0 },
  panelHeaderText: { fontSize: "0.65em", fontWeight: "800", color: "#fff", letterSpacing: "0.12em", textTransform: "uppercase" },
  servingRow: { display: "flex", alignItems: "center", gap: "18px", padding: "16px 24px" },
  footer: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 24px", flexShrink: 0, gap: "16px" },
  errorRibbon: { background: "rgba(239,68,68,0.15)", color: "#EF4444", fontSize: "0.75em", fontWeight: "600", padding: "6px 20px", textAlign: "center", flexShrink: 0 },
  pageBtn: { background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "4px", color: "#fff", width: "24px", height: "24px", cursor: "pointer", fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 },
};
